// The Zoo skin on the River: the day's creatures react to the loop everyone is hearing, driven by
// the world clock (no audio needed to animate; they hop in sync once you join).
import { useEffect, useMemo, useState } from "react";
import {
  SONG_TICKS,
  songEvents,
  periodSpec,
  TICKS_PER_BAR,
  type LoopData,
  type NoteEvent,
  type PeriodDef,
  type PeriodSpec,
} from "@eb/shared";
import { clock } from "../stream";
import { fetchKit } from "../audio/host";
import type { RiverPlayer } from "../river/player";
import { ZooStage, type ZooClock } from "../zoo";
import { useCalm, zooScene } from "./zooScene";

const { barAt, barContent, MS_PER_BAR } = clock;

export function ZooRiver({ player, period }: { player: RiverPlayer; period: PeriodDef }) {
  const calm = useCalm();
  const [spec, setSpec] = useState<PeriodSpec | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchKit(period.kit).then(({ manifest }) => alive && setSpec(periodSpec(period, manifest)));
    return () => {
      alive = false;
    };
  }, [period]);

  // The loop sounding now (refreshed when the slot changes).
  const current = useMemo(() => ({ key: "", events: [] as readonly NoteEvent[] }), []);
  const clock: ZooClock = useMemo(
    () => ({
      position() {
        const now = player.worldNow();
        const epoch = player.epochMs();
        const bar = barAt(now, epoch);
        const c = barContent(bar);
        const inBar = ((now - epoch - bar * MS_PER_BAR) / MS_PER_BAR) * TICKS_PER_BAR;
        if (c.kind !== "loop") return { tick: 0, lengthTicks: SONG_TICKS, playing: false };
        const key = `${c.day}:${c.slot}`;
        if (current.key !== key) {
          current.key = key;
          current.events = [];
          void player
            .lineup(c.day)
            .then((l) => player.loop(l.entries[c.slot].loopId))
            .then((loop?: LoopData) => {
              if (current.key === key && loop) current.events = songEvents(loop);
            })
            .catch(() => undefined);
        }
        return { tick: c.loopBar * TICKS_PER_BAR + inBar, lengthTicks: SONG_TICKS, playing: true };
      },
      events: () => current.events,
    }),
    [player, current],
  );
  if (!spec) return null;
  return (
    <ZooStage
      scene={zooScene(period, spec)}
      clock={clock}
      calm={calm}
      height={240}
      className="zoo-stage"
      label="The day's creatures, one per sound, dancing to the song that's playing"
    />
  );
}
